import React, { useState } from 'react';
import { clockTime } from '../../function-utils/building-time';
import styled from '@emotion/styled';
import {
  palette,
  spacing,
  radius,
  shadow,
  typography,
  duration,
  easing,
} from '../../style-utils/theme';
import { listItemMotion, m } from '../../motion';
import {
  Pill,
  Tag,
} from '../pages/dashboard/dashboard-units-components/dashboard-units.styled';
import { GhostButton, Band } from './iq-shared.styled';
import {
  IqEntry,
  IqStatus,
  STATUS_LABEL,
} from '../../interfaces/iq-interfaces';

const toneFor = (s: IqStatus): 'success' | 'warning' | 'alert' =>
  s === 'incident' ? 'alert' : s === 'follow_up' ? 'warning' : 'success';

const LogWrap = styled.div`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  overflow: hidden;
`;

const LogTitle = styled.div`
  padding: ${spacing.sm} ${spacing.md};
  font-family: ${typography.ui};
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
  border-bottom: 1px solid ${palette.line};
`;

/* A motion element so a page of rows arrives with the capped cascade; the
   flash (a citation chip scrolled here) is still a plain colour transition at
   the slow tempo — it is a highlight, not an entrance. */
const Row = m.div<{ $flash?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.sm} ${spacing.md};
  border-top: 1px solid ${palette.line};
  background: ${p => (p.$flash ? palette.tealTintSoft : 'transparent')};
  transition: background-color ${duration.slow} ${easing.standard};
  &:first-of-type {
    border-top: none;
  }
`;

const Line1 = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

const Time = styled.span`
  font-family: ${typography.mono};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

const Body = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
  margin: 0;
  line-height: ${typography.leading.normal};
`;

const Meta = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

const fmtTime = (iso: string, tz: string): string => clockTime(iso, tz);

interface Props {
  entries: IqEntry[];
  tz: string;
  truncated: boolean;
  entryLimit: number;
  total: number;
  flashId?: string | null;
}

const PAGE = 100;

export const IqRawLog: React.FC<Props> = ({
  entries,
  tz,
  truncated,
  entryLimit,
  total,
  flashId,
}) => {
  const [shown, setShown] = useState(PAGE);
  const rows = entries.slice(0, shown);

  return (
    <LogWrap>
      <LogTitle>Full log · {entries.length} entries</LogTitle>
      {truncated ? (
        <div style={{ padding: 12 }}>
          <Band $tone="warning">
            Showing the first {entryLimit} of {total} entries.
          </Band>
        </div>
      ) : null}
      {rows.map((e, i) => (
        <Row
          key={e.id}
          id={`iq-entry-${e.id}`}
          $flash={flashId === e.id}
          /* Index WITHIN the page: "Show more" appends a hundred rows, and the
             cascade restarts from 0 for them (capped at STAGGER.maxItems)
             rather than waiting out delays for rows already on screen. No
             layout animation — the log only grows. */
          {...listItemMotion(i % PAGE)}
          layout={false}
        >
          <Line1>
            <Time>{fmtTime(e.at, tz)}</Time>
            <Tag tone="neutral">
              {e.kind}
              {e.subKind ? ` · ${e.subKind}` : ''}
            </Tag>
            {e.location ? <Meta>{e.location}</Meta> : null}
            {e.people.length ? <Meta>{e.people.join(', ')}</Meta> : null}
            <Pill tone={toneFor(e.severity)}>{STATUS_LABEL[e.severity]}</Pill>
            {e.flaggedByKeyword ? <Meta>flagged by keyword</Meta> : null}
          </Line1>
          {e.text.length ? <Body>{e.text.join(' · ')}</Body> : null}
          {e.authorId ? <Meta>recorded by {e.authorId}</Meta> : null}
        </Row>
      ))}
      {entries.length > shown ? (
        <div style={{ padding: 12 }}>
          <GhostButton type="button" onClick={() => setShown(s => s + PAGE)}>
            Show more
          </GhostButton>
        </div>
      ) : null}
    </LogWrap>
  );
};
